import { IPromise } from 'angular';
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { style } from 'typestyle';

import { ButtonType, ButtonWidget, TextAreaWidget, TabStyles } from '@tableau/widgets';

import { Messages, Formatters } from '../../../codegen/Localize';
import { ContentDetailSection } from './ContentDetailSection';
import { IContentInfo } from '../../../ts/models/IContentInfo';
import { ExplorableItemType } from '../../../ts/models/ContentType';
import { MapDispatchToProps } from '../../utils/ConnectUtils';
import { ACTION_DIALOG_TESTID } from './ContentDetailsDialog';
import { MarkupField } from '../../spaces/Components/MarkupField';
import { incrementRouterSpaceReactKey } from '../../actions/spaces/IncrementRouterSpaceReactKeyAction';
import { UpdateCollectionsResponseData } from '../../spaces/Collections/CollectionModels';
import { ToasterService, ToastType } from '../../../ts/services/ToasterService';

export const DESCRIPTION_MAX_LENGTH: number = 255;

interface DescriptionDetailSectionOwnProps {
  readonly content: IContentInfo<ExplorableItemType>;
  readonly isActionEnabled: boolean;
  readonly setDescription: (description: string) => IPromise<any>;
}

interface DescriptionDetailDispatchProps {
  readonly incrementRouterSpaceReactKey: () => void;
}

export type DescriptionDetailSectionProps = DescriptionDetailSectionOwnProps & DescriptionDetailDispatchProps;

export function DescriptionDetailSection(props: DescriptionDetailSectionProps): JSX.Element {

  const [isSaving, setIsSaving] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);
  const [displayedDescription, setDisplayedDescription] = React.useState(props.content.description);

  const onSave = React.useCallback(() => {
    if (displayedDescription === props.content.description) {
      return;
    }

    setIsSaving(true);
    props.setDescription(displayedDescription)
      .then((response: UpdateCollectionsResponseData) => {
        if (response.errors && response.errors.length > 0) {
          ToasterService.instance.pop({
            type: ToastType.Error,
            text: Messages.collectionSetDescriptionError()
          });
          return; // keep dialog open
        }
        props.incrementRouterSpaceReactKey();
      }).finally(() => {
        setIsSaving(false);
      });
  }, [displayedDescription]);

  const onCancel = React.useCallback(() => {
    setIsEditing(false);
    setDisplayedDescription(props.content.description);
  }, []);

  return (
    <ContentDetailSection
      header={Messages.about()}
      detail={
        <Detail
          isEditing={isEditing}
          isSaving={isSaving}
          displayedDescription={displayedDescription}
          onChange={setDisplayedDescription}
        />
      }
      action={props.isActionEnabled &&
        <ActionButtons
          isEditing={isEditing}
          isSaving={isSaving}
          onEdit={() => setIsEditing(true)}
          onCancel={onCancel}
          onSave={onSave}
        />
      }
    />
  );
}

interface DetailProps {
  isEditing: boolean;
  isSaving: boolean;
  displayedDescription: string;
  onChange: (e: any) => void;
}

function Detail(props: DetailProps): JSX.Element {
  if (!props.isEditing) {
    return <MarkupField testId={'detail-dialog-description'} markup={props.displayedDescription} />;
  }

  const displayedDescriptionLength = props.displayedDescription? props.displayedDescription.length : 0;
  return (
    <TextAreaWidget
      label=""
      testId="description-editor"
      value={props.displayedDescription || ''}
      onChange={e => props.onChange(e.target.value)}
      message={Messages.remainingCharacters({
        COUNT: DESCRIPTION_MAX_LENGTH - displayedDescriptionLength,
        COUNT_LOC: Formatters.formatCount(DESCRIPTION_MAX_LENGTH - displayedDescriptionLength)
      })}
      maxLength={DESCRIPTION_MAX_LENGTH}
      rows={8}
      style={{
        width: '100%',
        resize: 'none',
        display: 'block'
      }}
      className={style({ width: 'calc(100% - 12px)' })}
      disabled={props.isSaving}
    />
  );
}

export interface ActionButtonsProps {
  isEditing: boolean;
  isSaving: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
}

export function ActionButtons(props: ActionButtonsProps): JSX.Element {
  if (props.isEditing) {
    return (
      <div style={{display: 'flex', flexDirection: 'column'}}>
        <ButtonWidget
          buttonType={ButtonType.Primary}
          children={Messages.save()}
          testId={`${ACTION_DIALOG_TESTID}-set-description-save`}
          handleClick={props.onSave}
          disabled={props.isSaving}
        />
        <ButtonWidget
          buttonType={ButtonType.Outline}
          children={Messages.cancel()}
          testId={`${ACTION_DIALOG_TESTID}-set-description-cancel`}
          handleClick={props.onCancel}
          disabled={props.isSaving}
          style={{marginTop: TabStyles.Spacing.MediumSpace}}
        />
      </div>
    );
  }

  return (
    <ButtonWidget
      buttonType={ButtonType.Outline}
      children={Messages.edit()}
      testId={`${ACTION_DIALOG_TESTID}-set-description-action`}
      handleClick={props.onEdit}
    />
  );
}


const mapDispatchToProps: MapDispatchToProps<DescriptionDetailDispatchProps> = (dispatch) => bindActionCreators({
  incrementRouterSpaceReactKey: incrementRouterSpaceReactKey
}, dispatch);

export const ConnectedDescriptionDetailSection: React.ComponentClass<DescriptionDetailSectionOwnProps> =
  connect(undefined, mapDispatchToProps)(DescriptionDetailSection);
